import { PublicLayout } from '../components/layouts/Layout'
import { Card } from '../components/common/UI'

export default function Terms() {
  return (
    <PublicLayout>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">BlastTime Terms of Service and End User License Agreement</h1>
            <p className="text-xl text-gray-600">Last Updated: September 17, 2025</p>
          </div>

          <Card className="p-8 mb-8">
            <div className="prose max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to BlastTime!</h2>
              <p className="text-gray-700 mb-6">
                These Terms of Service ("Terms") and End User License Agreement ("EULA") govern your use of BlastTime and its features including ride-sharing, events, clubs, groups, and social connections. By downloading, installing, or using our application, you agree to be bound by these Terms.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-700 mb-6">
                Our mission is to give people the power to build authentic community connections and facilitate safe, convenient ride-sharing while protecting your privacy and keeping your data secure within our platform.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">What We Provide</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Core Features</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li><strong>Social Connections:</strong> We help you find and connect with people, groups, and organizations that matter to you using only the data you choose to share within our app</li>
                <li><strong>Ride-Sharing:</strong> Connect with other users for safe, convenient transportation through our integrated ride-sharing features using live location sharing</li>
                <li><strong>Events & Activities:</strong> Discover and participate in local events, join clubs based on your interests, and create or join groups with like-minded individuals</li>
                <li><strong>Personalized Experience:</strong> We use data from your interactions within BlastTime - such as your connections, settings, and activity - to personalize your experience exclusively within our app</li>
                <li><strong>Safety & Community Protection:</strong> We monitor for harmful conduct and take appropriate action including content removal, feature restrictions, account suspension, or contacting law enforcement when necessary</li>
                <li><strong>Advanced Technologies:</strong> We use technologies like artificial intelligence and machine learning to provide safe, functional services for all users regardless of physical ability or geographic location</li>
                <li><strong>Continuous Improvement:</strong> We research and analyze how users interact with our features to improve the BlastTime experience while keeping all data within our platform</li>
                <li><strong>Global Service:</strong> To operate our service, we store and distribute content in our secure data centers, which may be located outside your country of residence. This infrastructure is operated by Xupoli Technologies, Inc.</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Software License and EULA Terms</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Grant of License</h3>
              <p className="text-gray-700 mb-4">
                Subject to your compliance with these Terms, Xupoli Technologies, Inc. grants you a limited, non-exclusive, non-transferable, revocable license to download, install, and use the BlastTime application solely for your personal, non-commercial use in accordance with these Terms.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">License Restrictions</h3>
              <p className="text-gray-700 mb-4">You may not:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Copy, modify, distribute, sell, or lease any part of our software or services</li>
                <li>Reverse engineer, decompile, or attempt to extract the source code of our software</li>
                <li>Create derivative works based on our application</li>
                <li>Use the software for any unlawful purpose</li>
                <li>Remove, alter, or obscure any proprietary notices on the software</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Updates and Modifications</h3>
              <p className="text-gray-700 mb-4">
                We may automatically update the software to provide bug fixes, security updates, and new features. By using our app, you consent to receive these automatic updates.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Termination of License</h3>
              <p className="text-gray-700 mb-6">
                This license terminates automatically if you violate these Terms. Upon termination, you must cease all use of the software and delete all copies from your devices.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Who Can Use BlastTime</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">You must:</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Be at least 13 years old</li>
                <li>Use your real name that you use in everyday life</li>
                <li>Provide accurate information about yourself</li>
                <li>Create only one account for personal use</li>
                <li>Keep your password secure and not share account access with others</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">You cannot use BlastTime if:</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>You are under 13 years old</li>
                <li>You are a convicted sex offender</li>
                <li>We've previously disabled your account for Terms or Policy violations</li>
                <li>You are prohibited from using our services under applicable laws</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy Choices and Data Protection</h2>
              <p className="text-gray-700 mb-4">
                At BlastTime, we value your privacy and are committed to giving you clear choices about how your personal data is collected, used, and shared — especially your location information, which is central to our features.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Location Data</h3>
              <p className="text-gray-700 mb-4"><strong>How We Use Location:</strong> We request access to your location in order to:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Show nearby users, events, or content</li>
                <li>Support check-ins, local feeds, and geo-tagged posts</li>
                <li>Improve in-app recommendations based on your area</li>
                <li>Enable ride-sharing features with live location sharing</li>
              </ul>

              <p className="text-gray-700 mb-6">
                We never access your location without permission. You can disable or adjust access at any time.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Privacy Choices</h3>
              <p className="text-gray-700 mb-4">You can manage your privacy in several ways:</p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li><strong>Edit Your Information:</strong> Update or delete personal details in your profile settings at any time</li>
                <li><strong>Control Permissions:</strong> Grant or revoke permissions (location, camera, notifications, contacts) through your device settings</li>
                <li><strong>Data Access & Deletion:</strong> Download a copy of your personal data or request permanent account deletion and data removal</li>
                <li><strong>Communication Preferences:</strong> Control what notifications and communications you receive from us through in-app settings</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">What You Can Share and Do</h2>
              <p className="text-gray-700 mb-4">
                We want BlastTime to be a place for authentic expression and meaningful connections while maintaining community safety and integrity.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Prohibited Activities</h3>
              <p className="text-gray-700 mb-4">You may not:</p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>Share unlawful, misleading, discriminatory, or fraudulent content</li>
                <li>Infringe on others' rights or intellectual property</li>
                <li>Upload viruses, malicious code, or anything that could harm our services</li>
                <li>Use automated tools to access or collect data without permission</li>
                <li>Access data you don't have permission to access</li>
                <li>Violate these Terms or our community policies</li>
                <li>Engage in harassment, bullying, or threatening behavior</li>
                <li>Share content that promotes violence or illegal activities</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Content and Our Permissions</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Ownership</h3>
              <p className="text-gray-700 mb-4">
                You own all content you create and share on BlastTime. You're free to share your content elsewhere as you choose.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">License to BlastTime</h3>
              <p className="text-gray-700 mb-4">
                When you share content protected by intellectual property rights (photos, videos, etc.), you grant us a non-exclusive, transferable, sub-licensable, royalty-free, worldwide license to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Host, store, and display your content</li>
                <li>Enable core app functionality (ride-sharing, events, groups, etc.)</li>
                <li>Provide technical services and platform features</li>
                <li>Ensure proper functioning across different devices and platforms</li>
              </ul>

              <p className="text-gray-700 mb-6">
                <strong>Important Notes:</strong> This license ends when you delete your account. We do not use your content for external advertising or share it with third parties.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Ride-Sharing Specific Terms</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Safety and Responsibility</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Users participating in ride-sharing do so at their own risk</li>
                <li>BlastTime facilitates connections but is not responsible for the actions of individual users</li>
                <li>Report safety concerns immediately through the app's safety features</li>
                <li>Follow all local transportation laws and regulations</li>
                <li>Verify identity of ride participants before sharing rides</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Location Sharing for Rides</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Live location sharing is required for ride-sharing features</li>
                <li>Location data is only shared with users you choose to connect with for rides</li>
                <li>You can control your location sharing settings at any time</li>
                <li>Location sharing for rides can be disabled separately from general location services</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Insurance and Liability</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>BlastTime does not provide insurance coverage for ride-sharing activities</li>
                <li>Users are responsible for ensuring appropriate insurance coverage</li>
                <li>Check with your insurance provider regarding coverage during ride-sharing</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">BlastTime's Content</h3>
              <p className="text-gray-700 mb-4">
                We retain all rights to BlastTime's design, features, logos, proprietary content, and software code. You cannot use our intellectual property without written permission.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Responsibilities</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>Respect others' intellectual property rights</li>
                <li>Only share content you have permission to share</li>
                <li>Report suspected intellectual property violations through our reporting system</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Disclaimers and Limitations</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Service Availability</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>BlastTime is provided "as is" without warranties of any kind</li>
                <li>We do not guarantee uninterrupted or error-free service</li>
                <li>Features may be modified, suspended, or discontinued at our discretion</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">User Interactions</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>We are not responsible for interactions between users</li>
                <li>Exercise caution when meeting users in person</li>
                <li>Report inappropriate behavior through our safety reporting tools</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Technical Issues</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>We are not liable for data loss, device damage, or other technical issues</li>
                <li>Regularly backup important information stored in the app</li>
                <li>Keep your app updated to the latest version for optimal performance</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Legal Terms</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Governing Law</h3>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of the State of New Jersey and the United States of America without regard to conflict of law principles.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Dispute Resolution</h3>
              <p className="text-gray-700 mb-4">
                Any disputes arising from these Terms or your use of BlastTime shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, conducted in New Jersey.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Entire Agreement</h3>
              <p className="text-gray-700 mb-6">
                These Terms constitute the complete agreement between you and Xupoli Technologies, Inc. regarding BlastTime usage, superseding all prior agreements.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes and Feedback</h2>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>We may need to change usernames in certain circumstances for safety or legal reasons</li>
                <li>We may use your feedback without compensation or confidentiality obligations</li>
                <li>We reserve all rights not expressly granted to you</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Amendments</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>Changes to these Terms must be in writing and signed by us</li>
                <li>We'll notify you of significant changes through the app and email</li>
                <li>Continued use of the app after changes constitutes acceptance of new Terms</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 mb-6">
                For questions about these Terms, privacy concerns, or to exercise your data rights:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> <a href="mailto:hello@xupoli.com" className="text-blue-600 hover:text-blue-800">hello@xupoli.com</a>
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Privacy Policy:</strong> <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800">https://blasttime.com/privacy-policy</a>
                </p>
                <p className="text-gray-700">
                  <strong>Support:</strong> Available through the app's Help section
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Effective Date and Updates</h2>
              <p className="text-gray-700 mb-6">
                These Terms are effective as of September 17, 2025, or the date you first download, install, or use BlastTime, whichever is later. We will notify users of significant changes to these Terms through the app, email notifications, and by updating the "Last Updated" date above.
              </p>
              <p className="text-gray-700 mb-6">
                Your continued use of BlastTime after any such changes constitutes your acceptance of the new Terms.
              </p>

              <div className="border-t border-gray-200 pt-6 mt-8">
                <p className="text-sm text-gray-500 text-center">
                  © 2025 BlastTime. All Rights Reserved<br />
                  Powered by: Xupoli Technologies Inc.<br />
                  Licensed Software Application
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PublicLayout>
  )
}